import React from 'react';
import { AreaChart, Area, XAxis, ResponsiveContainer } from 'recharts';

/**
 * Decorative "Performance Overview" dashboard card shown on the right of the
 * landing-page hero. Purely illustrative — it does NOT render any real
 * strategy's numbers, so it carries no compliance implications. The curve is a
 * fixed, deterministic shape (no Math.random) so the hero looks identical on
 * every render.
 */

// Deterministic upward-drifting equity shape (illustrative only).
const CURVE = [
  2, 1, 3, 2, 4, 6, 5, 8, 7, 10, 9, 13, 12, 11, 14, 17, 16, 19, 18, 22,
  21, 20, 24, 23, 27, 26, 30, 29, 33, 36,
].map((v, i) => ({ i, v }));

const TILES = [
  { label: 'Total Return', value: '+24.73%', tone: 'text-green-600 dark:text-green-400' },
  { label: 'Win Rate', value: '68.4%', tone: 'text-gray-900 dark:text-white' },
  { label: 'Max Drawdown', value: '-8.21%', tone: 'text-red-500 dark:text-red-400' },
  { label: 'Trades', value: '392', tone: 'text-gray-900 dark:text-white' },
];

const HeroDashboardMock: React.FC = () => {
  return (
    <div className="rounded-2xl bg-white dark:bg-gray-900 shadow-xl ring-1 ring-gray-200/70 dark:ring-gray-800 overflow-hidden">
      {/* Window chrome */}
      <div className="flex items-center gap-1.5 px-4 py-3 border-b border-gray-100 dark:border-gray-800">
        <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
        <span className="h-2.5 w-2.5 rounded-full bg-yellow-400" />
        <span className="h-2.5 w-2.5 rounded-full bg-green-400" />
      </div>

      <div className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Performance Overview</h3>
          <span className="text-xs text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-700 rounded-md px-2 py-1">
            Last 3 Months
          </span>
        </div>

        {/* Stat tiles */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mb-5">
          {TILES.map((t) => (
            <div key={t.label} className="rounded-lg border border-gray-100 dark:border-gray-800 p-3">
              <p className="text-[0.65rem] text-gray-500 dark:text-gray-400 leading-tight">{t.label}</p>
              <p className={`text-base font-bold tabular-nums mt-1 ${t.tone}`}>{t.value}</p>
            </div>
          ))}
        </div>

        {/* Equity curve */}
        <div className="rounded-lg bg-gray-50 dark:bg-gray-800/40 p-3">
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Equity Curve</p>
          <ResponsiveContainer width="100%" height={150}>
            <AreaChart data={CURVE} margin={{ top: 4, right: 4, left: 4, bottom: 0 }}>
              <defs>
                <linearGradient id="hero-equity" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0037B7" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="#0037B7" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="i" hide />
              <Area
                type="monotone"
                dataKey="v"
                stroke="#0037B7"
                strokeWidth={2.5}
                fill="url(#hero-equity)"
                isAnimationActive={false}
                dot={false}
              />
            </AreaChart>
          </ResponsiveContainer>
          <div className="flex justify-between text-[0.65rem] text-gray-400 dark:text-gray-500 mt-1 px-1">
            <span>Mar</span>
            <span>Apr</span>
            <span>May</span>
          </div>
        </div>

        {/* Compliance disclaimer — pinned to the bottom of the card */}
        <p className="mt-3 text-[0.65rem] leading-snug text-gray-400 dark:text-gray-500 text-center">
          Disclaimer: Illustrative visualization only. Not actual, live, or backtested performance.
        </p>
      </div>
    </div>
  );
};

export default HeroDashboardMock;
